import { useCallback, useEffect, useState } from 'react';
import {
  AppProvider,
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DataTable,
  FormLayout,
  InlineGrid,
  InlineStack,
  Page,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import type { BadgeProps } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

// ---------------------------------------------------------------------------
// Owner dashboard — shown at app.merchantkits.com when opened outside Shopify.
// Auth is an httpOnly cookie set by POST /api/admin/login.
// ---------------------------------------------------------------------------

interface StoreRow {
  domain: string;
  name: string | null;
  email: string | null;
  subscriptionStatus: string;
  installed: boolean;
  inTrial: boolean;
  trialEndsAt: string | null;
  installedAt: string;
  uninstalledAt: string | null;
  templates: number;
  documentsSent: number;
  documentsSigned: number;
  lastSentAt: string | null;
  sentThisMonth: number;
  plan: string;
  monthlyLimit: number;
  countingSince: string | null;
  testCharge: boolean;
  canDelete: boolean;
  multiSignerEnabled: boolean;
}

interface Stats {
  generatedAt: string;
  monthlyLimit: number;
  stores: {
    totalEverInstalled: number;
    currentlyInstalled: number;
    uninstalled: number;
    activeSubscriptions: number;
    inTrial: number;
    testStores: number;
    paying: number;
    estimatedMrrUsd: number;
  };
  documents: Record<string, number> & { total: number; sentLast30Days: number; signedLast30Days: number };
  storeList: StoreRow[];
}

async function adminFetch(path: string, init?: RequestInit) {
  const res = await fetch(`/api/admin${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.message ?? `Request failed (${res.status})`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return body;
}

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

function storeBadge(s: StoreRow): { tone: BadgeProps['tone']; label: string } {
  if (!s.installed) return { tone: undefined, label: 'Uninstalled' };
  if (s.subscriptionStatus === 'ACTIVE') {
    if (s.testCharge) return { tone: undefined, label: 'Active' };
    return s.inTrial ? { tone: 'info', label: 'Trial' } : { tone: 'success', label: 'Paying' };
  }
  if (s.subscriptionStatus === 'PENDING' || s.subscriptionStatus === 'NONE') {
    return { tone: 'attention', label: 'Not approved' };
  }
  return { tone: 'critical', label: s.subscriptionStatus.toLowerCase().replace(/^./, (c) => c.toUpperCase()) };
}

// ---------------------------------------------------------------------------

function LoginScreen({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await adminFetch('/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      setPassword('');
      onLoggedIn();
    } catch (e: any) {
      setError(e.status === 400 ? 'Enter a valid email and password.' : e.message);
    } finally {
      setLoading(false);
    }
  }, [email, password, onLoggedIn]);

  return (
    <Box paddingBlockStart="1600" paddingInline="400">
      <div style={{ maxWidth: 400, margin: '0 auto' }}>
        <Card>
          <BlockStack gap="400">
            <InlineStack gap="200" blockAlign="center">
              <img src="/logo-icon.png" alt="" style={{ width: 28, height: 28 }} />
              <Text as="h1" variant="headingLg">SignShield Admin</Text>
            </InlineStack>
            {error && <Banner tone="critical">{error}</Banner>}
            <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <FormLayout>
                <TextField label="Email" type="email" value={email} onChange={setEmail} autoComplete="username" />
                <TextField label="Password" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
                <Button variant="primary" submit loading={loading} fullWidth>Log in</Button>
              </FormLayout>
            </form>
          </BlockStack>
        </Card>
      </div>
    </Box>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" tone="subdued">{label}</Text>
        <Text as="p" variant="heading2xl">{value}</Text>
        {hint && <Text as="p" variant="bodySm" tone="subdued">{hint}</Text>}
      </BlockStack>
    </Card>
  );
}

function Dashboard({ stats, onRefresh, onLogout, onResetUsage, onDeleteData, refreshing }: {
  stats: Stats;
  onRefresh: () => void;
  onLogout: () => void;
  onResetUsage: (s: StoreRow) => void;
  onDeleteData: (s: StoreRow) => void;
  refreshing: boolean;
}) {
  const { stores, documents } = stats;
  const rows = stats.storeList.map((s) => {
    const badge = storeBadge(s);
    return [
      <BlockStack gap="050" key={s.domain}>
        <Text as="span" fontWeight="semibold">{s.name ?? s.domain}</Text>
        <Text as="span" variant="bodySm" tone="subdued">{s.domain}</Text>
        {s.email && <Text as="span" variant="bodySm" tone="subdued">{s.email}</Text>}
        {s.canDelete && (
          <Box><Button variant="plain" tone="critical" size="slim" onClick={() => onDeleteData(s)}>Delete data</Button></Box>
        )}
      </BlockStack>,
      <BlockStack gap="050" key="b">
        <InlineStack gap="100"><Badge tone={badge.tone}>{badge.label}</Badge>{s.testCharge && s.installed && <Badge>Test</Badge>}</InlineStack>
        {s.installed && s.subscriptionStatus === 'ACTIVE' && <Text as="span" variant="bodySm" tone="subdued">{s.plan.replace('SignShield ', '')}</Text>}
      </BlockStack>,
      s.inTrial ? `Ends ${fmtDate(s.trialEndsAt)}` : '—',
      fmtDate(s.installedAt),
      s.templates,
      <BlockStack gap="050" key="m" inlineAlign="end">
        <Text as="span" tone={s.sentThisMonth >= s.monthlyLimit ? 'critical' : undefined}>
          {s.sentThisMonth} / {s.monthlyLimit}
        </Text>
        {s.countingSince && (
          <Text as="span" variant="bodySm" tone="subdued">since {fmtDate(s.countingSince)}</Text>
        )}
        {s.installed && s.sentThisMonth > 0 && (
          <Button variant="plain" size="slim" onClick={() => onResetUsage(s)}>Reset count</Button>
        )}
      </BlockStack>,
      s.documentsSent,
      s.documentsSigned,
      fmtDate(s.lastSentAt),
    ];
  });

  return (
    <Page
      title="SignShield Admin"
      subtitle={`Updated ${new Date(stats.generatedAt).toLocaleString()}`}
      primaryAction={{ content: 'Refresh', onAction: onRefresh, loading: refreshing }}
      secondaryActions={[{ content: 'Log out', onAction: onLogout }]}
    >
      <BlockStack gap="500">
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Stores</Text>
          <InlineGrid columns={{ xs: 2, md: 4 }} gap="400">
            <StatCard label="Installed now" value={stores.currentlyInstalled} hint={`${stores.totalEverInstalled} ever · ${stores.uninstalled} uninstalled`} />
            <StatCard label="Active subscriptions" value={stores.activeSubscriptions} hint={`${stores.inTrial} in trial${stores.testStores ? ` · ${stores.testStores} test (not billed)` : ''}`} />
            <StatCard label="Paying" value={stores.paying} hint="Trial ended, still subscribed" />
            <StatCard label="Est. monthly revenue" value={`$${stores.estimatedMrrUsd.toFixed(2)}`} hint="Before Shopify's share" />
          </InlineGrid>
        </BlockStack>

        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Documents</Text>
          <InlineGrid columns={{ xs: 2, md: 4 }} gap="400">
            <StatCard label="Sent (all time)" value={documents.total} hint={`${documents.sentLast30Days} in last 30 days`} />
            <StatCard label="Signed" value={documents.SIGNED ?? 0} hint={`${documents.signedLast30Days} in last 30 days`} />
            <StatCard label="Awaiting signature" value={(documents.PENDING ?? 0) + (documents.VIEWED ?? 0)} hint={`${documents.VIEWED ?? 0} opened`} />
            <StatCard label="Declined / expired" value={(documents.DECLINED ?? 0) + (documents.EXPIRED ?? 0)} />
          </InlineGrid>
        </BlockStack>

        <Card padding="0">
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'text']}
            headings={['Store', 'Status', 'Trial', 'Installed', 'Templates', 'This month', 'Sent (all)', 'Signed', 'Last sent']}
            rows={rows}
            verticalAlign="middle"
          />
        </Card>
        <Text as="p" variant="bodySm" tone="subdued">
          Development and review stores (e.g. signshield-dev) are included and never pay. Confirm real
          earnings in the Shopify Partner Dashboard.
        </Text>
      </BlockStack>
    </Page>
  );
}

export default function AdminApp() {
  const [state, setState] = useState<'loading' | 'login' | 'ready'>('loading');
  const [stats, setStats] = useState<Stats | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      setStats(await adminFetch('/stats'));
      setState('ready');
    } catch (e: any) {
      if (e.status === 401) setState('login');
      else { setError(e.message); setState((s) => (s === 'loading' ? 'login' : s)); }
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'SignShield Admin';
    load();
  }, [load]);

  const resetUsage = useCallback(async (s: StoreRow) => {
    const ok = window.confirm(
      `Give ${s.name ?? s.domain} a fresh ${s.monthlyLimit} signing requests from now until the 1st of next month?\n\n` +
      `The ${s.sentThisMonth} already sent this month will stop counting.`,
    );
    if (!ok) return;
    try {
      await adminFetch(`/stores/${encodeURIComponent(s.domain)}/reset-usage`, { method: 'POST' });
      await load();
    } catch (e: any) {
      if (e.status === 401) setState('login');
      else setError(e.message);
    }
  }, [load]);

  const deleteData = useCallback(async (s: StoreRow) => {
    const typed = window.prompt(
      `Permanently delete all data for ${s.name ?? s.domain}?\n\n` +
      `This removes its ${s.templates} template(s), ${s.documentsSent} sent document(s) and signed PDFs. ` +
      `It cannot be undone. If the store installs SignShield again, it starts fresh with a new 7-day trial.\n\n` +
      `Type ${s.domain} to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== s.domain) {
      window.alert('The store address did not match. Nothing was deleted.');
      return;
    }
    try {
      await adminFetch(`/stores/${encodeURIComponent(s.domain)}/delete-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmDomain: typed.trim() }),
      });
      await load();
    } catch (e: any) {
      if (e.status === 401) setState('login');
      else setError(e.message);
    }
  }, [load]);

  const logout = useCallback(async () => {
    await adminFetch('/logout', { method: 'POST' }).catch(() => {});
    setStats(null);
    setState('login');
  }, []);

  return (
    <AppProvider i18n={enTranslations}>
      {error && <Box padding="400"><Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner></Box>}
      {state === 'loading' && (
        <Box paddingBlockStart="1600"><InlineStack align="center"><Spinner /></InlineStack></Box>
      )}
      {state === 'login' && <LoginScreen onLoggedIn={load} />}
      {state === 'ready' && stats && (
        <Dashboard stats={stats} onRefresh={load} onLogout={logout} onResetUsage={resetUsage} onDeleteData={deleteData} refreshing={refreshing} />
      )}
    </AppProvider>
  );
}
